import { Button, Card, CurrencyInput, formatDate, Hint, Modal, Select, useLoading, ValueOpt } from "best-common-react";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  billingTransaction,
  getLastChargeForPurchaseEntry,
  updatePurchaseOpportunityEntry,
} from "../../api/RequesTixApi";
import { PurchaseOpportunityStatusIds } from "../../constants/PurchaseOpportunityConstants";
import { AdminPurchaseOpportunityEntryDTO, PurchaseOpportunityStatus } from "../../types/PurchaseOpportunity";
import { TransactionHistory } from "../../types/Transaction";
import { BillingTransactionDTO } from "../../types/UserBilling";
import CreditCardDisplay from "../payment/CreditCardDisplay";
import { useDropdowns } from "../../contexts/DropdownsContext";
import { queries } from "../../queries";
import { useQueryClient } from "@tanstack/react-query";
const Container = styled.div`
  width: 600px;
  margin-right: 1rem;
  margin-bottom: 1rem;
`;

type AdminPurchaseOpportunityGameFormProps = {
  entry: AdminPurchaseOpportunityEntryDTO;
  refresh: () => Promise<any>;
  formChanged: boolean;
};

type ChargeModalProps = {
  isCharge: boolean;
  entry: AdminPurchaseOpportunityEntryDTO;
  open: boolean;
  setOpen: (value: boolean) => void;
  refresh: () => Promise<any>;
};

const ChargeModal: React.FC<ChargeModalProps> = ({ isCharge, entry, open, setOpen, refresh }) => {
  const { setLoading } = useLoading();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState<number>(0);

  const close = () => {
    setOpen(false);
  };

  const sendTransaction = async () => {
    try {
      setLoading(true);
      const txDTO: BillingTransactionDTO = {
        purchaseEntryId: entry.purchaseOpportunityEntryId,
        userBillingId: entry.userBilling.userBillingId,
        amount: amount,
        charge: isCharge,
        paidByBoc: true,
      };
      await billingTransaction(txDTO);

      // Invalidate queries after transaction
      await queryClient.invalidateQueries({
        queryKey: queries.purchaseOpportunity.getAdminPurchaseOpportunity(entry.purchaseOpportunityId).queryKey,
      });

      await queryClient.invalidateQueries({
        queryKey: queries.purchaseOpportunity._def,
      });

      close();
      await refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviousCharge = async (entryId: number) => {
    try {
      setLoading(true);
      const res: TransactionHistory = await getLastChargeForPurchaseEntry(entryId);
      setAmount(res.amount);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isCharge) {
      void fetchPreviousCharge(entry.purchaseOpportunityEntryId);
    }
  }, [isCharge, entry]);

  return (
    <Modal show={open}>
      <Modal.Header close={close}>{isCharge ? "Charge" : "Refund"} Card</Modal.Header>
      <Modal.Body>
        <CurrencyInput
          id="chargeAmount"
          value={amount}
          onChange={(value: number) => setAmount(value)}
          label={isCharge ? "Charge Amount" : "Refund Amount"}
          disabled={!isCharge}
          required
        />
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end">
          <Button className="bcr-me-2" variant="primary" onClick={sendTransaction} disabled={amount <= 0}>
            {isCharge ? "Charge" : "Refund"}
          </Button>
          <Button onClick={close}>Cancel</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const AdminPurchaseOpportunityGameForm: React.FC<AdminPurchaseOpportunityGameFormProps> = ({
  entry,
  refresh,
  formChanged,
}) => {
  const { setLoading } = useLoading();
  const { purchaseOpportunityStatusOptions } = useDropdowns();
  const [hasChanged, setHasChange] = useState<boolean>(false);
  const [localEntry, setLocalEntry] = useState<AdminPurchaseOpportunityEntryDTO>();
  const [chargeOpen, setChargeOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const changeStatus = (value: ValueOpt<PurchaseOpportunityStatus>) => {
    setHasChange(true);
    setLocalEntry({
      ...localEntry!,
      purchaseOpportunityStatus: value.value,
    });
  };

  const saveEntry = async () => {
    try {
      setLoading(true);
      await updatePurchaseOpportunityEntry(localEntry!);

      // Invalidate the specific purchase opportunity query
      await queryClient.invalidateQueries({
        queryKey: queries.purchaseOpportunity.getAdminPurchaseOpportunity(localEntry!.purchaseOpportunityId).queryKey,
      });

      // Also invalidate any broader purchase opportunity queries that might exist
      await queryClient.invalidateQueries({
        queryKey: queries.purchaseOpportunity._def,
      });

      await refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLocalEntry(entry);
  }, [entry]);

  return (
    <Container>
      <Card>
        <Card.Header>Game {entry.gameNumber}</Card.Header>
        <Card.Body>
          {!!localEntry && (
            <>
              <ChargeModal
                isCharge={
                  localEntry.purchaseOpportunityStatus.purchaseOpportunityStatusId ===
                  PurchaseOpportunityStatusIds.SELECTED
                }
                entry={entry}
                refresh={refresh}
                open={chargeOpen}
                setOpen={setChargeOpen}
              />
              <Select
                id={`${entry.purchaseOpportunityEntryId}-game-status`}
                options={purchaseOpportunityStatusOptions}
                value={purchaseOpportunityStatusOptions.find(
                  (opt: ValueOpt<PurchaseOpportunityStatus>): boolean =>
                    opt.value.purchaseOpportunityStatusId ===
                    localEntry.purchaseOpportunityStatus.purchaseOpportunityStatusId,
                )}
                label="Entry Status"
                required
                onChange={changeStatus}
                gutterBottom
              />
              {[
                PurchaseOpportunityStatusIds.SELECTED,
                PurchaseOpportunityStatusIds.CHARGED,
                PurchaseOpportunityStatusIds.REFUNDED,
              ].includes(localEntry.purchaseOpportunityStatus.purchaseOpportunityStatusId) && (
                <div>
                  <div className="d-flex">
                    <CreditCardDisplay card={localEntry.userBilling} />
                    {!!entry.lastChargedDate && (
                      <div className="bcr-ms-1">
                        <Hint>Charged on: {formatDate(new Date(entry.lastChargedDate))}</Hint>
                      </div>
                    )}
                  </div>

                  <Button
                    className="bcr-mt-1"
                    variant="secondary"
                    onClick={() => setChargeOpen(true)}
                    disabled={formChanged}
                  >
                    {localEntry.purchaseOpportunityStatus.purchaseOpportunityStatusId ===
                    PurchaseOpportunityStatusIds.CHARGED
                      ? "Refund"
                      : "Charge"}
                    &nbsp;Card
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
        <Card.Footer>
          <Button variant="primary" disabled={!hasChanged} onClick={saveEntry}>
            Save
          </Button>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default AdminPurchaseOpportunityGameForm;
